import React, { useState, useEffect } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import store, { noticeActions } from '@apply/adminStore';
import { useSelector } from 'react-redux';
import { FileSelectDialog } from '../../common/SharedComponents';
import { NoticesApi, FileApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { LoadPanel } from 'devextreme-react/load-panel';
import { NoticeTypeOptions, NoticeStatusTypeOptions, NoticeStatusType } from '@apply/constants';
import { formatDate, formatDateTime } from '../../common/SharedFunctions';
import { RefreshEvents } from '../../common/SharedEvents';
import { handleAdminError, handleAdminSuccess } from '../../common/SharedFunctions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

const ViewNoticeModal = () => {
    const { isOpen, noticeId } = useSelector((state) => state.notices.viewNoticeModal);
    const { dispatch } = store;

    const noticesApi = new NoticesApi.NoticesApi(applicationInsights);
    const fileApi = new FileApi.FileApi(applicationInsights);
    const refreshEvents = new RefreshEvents();

    const [notice, set_notice] = useState(null);
    const [noticeFile, set_noticeFile] = useState(null);
    const [signedFile, set_signedFile] = useState(null);

    //File Upload
    const fileUploadOptions = {
        isOpen: false,
        maxFiles: 1,
        isReplace: false,
        typeName: '',
    };
    const [fileSelectOptions, set_fileSelectOptions] = useState(fileUploadOptions);

    const [loadingOpen, set_loadingOpen] = useState(false);
    const [loadingMessage, set_loadingMessage] = useState('Loading...');

    const styles = {
        buttonMarginRight: { marginRight: '10px' },
        buttonContainer: {
            textAlign: 'right',
            borderTop: '2px solid silver',
            padding: '10px 10px',
        },
        form: { paddingLeft: '5px', paddingTop: '10px' },
        formRow: { marginBottom: '0' },
        tablerow: { border: 'none', cursor: 'default' },
        sectionHeader: { margin: '10px 0' },
    };

    useEffect(() => {
        async function noticeIdChanged() {
            resetState();

            if (!isOpen || !noticeId) {
                return;
            }

            if (noticeId && noticeId > 0) {
                await loadNoticeInfo(noticeId);
            }
        }

        noticeIdChanged();
    }, [isOpen, noticeId]);

    const _handleClose = () =>
        dispatch(
            noticeActions.setViewNoticeModal({
                viewNoticeModal: { isOpen: false },
            })
        );

    const _refresh = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        await loadNoticeInfo(noticeId);
    };

    const loadNoticeInfo = async (id) => {
        try {
            set_loadingMessage('Loading...');
            set_loadingOpen(true);

            const response = await noticesApi.getNoticeById(id);
            if (response.statusCode !== 200) {
                return;
            }
            const ln = response.result;
            set_notice(ln);
        } finally {
            set_loadingOpen(false);
        }
    };

    const resetState = () => {
        set_notice(null);
        set_noticeFile(null);
        set_signedFile(null);
        set_loadingOpen(false);
    };

    const canSubmitUpload = () => {
        let isOk = noticeFile || signedFile;
        return isOk;
    };

    const uploadFileAndEmail = async (e, email) => {
        e.preventDefault();
        e.stopPropagation();

        if (!noticeFile && !signedFile) {
            return;
        }

        try {
            if (noticeFile) {
                set_loadingMessage('Saving Notice file...');
                set_loadingOpen(true);

                const model = { id: notice.id };
                const upLoadResult = await fileApi.uploadTempFiles([noticeFile]);
                let result = upLoadResult.result;
                if (result && result.length > 0) {
                    model.tempFileName = result[0].tempName;
                }

                set_loadingMessage('Saving...');
                set_loadingOpen(true);
                const response = await noticesApi.updateNoticeFile(model);
                if (response.statusCode !== 200) {
                    return;
                }
            }

            if (signedFile) {
                set_loadingMessage('Saving Signed file...');
                set_loadingOpen(true);

                const response = await noticesApi.uploadSignedNoticeFile(notice.id, signedFile);
                if (response.statusCode !== 200) {
                    return;
                }
            }

            set_noticeFile(null);
            set_signedFile(null);

            let wasEmailed = false;
            if (notice.canEmail && email) {
                wasEmailed = await emailNotice(notice.id, true);
                if (!wasEmailed) {
                    return;
                }
            }

            await loadNoticeInfo(notice.id);
            refreshEvents.dispatchRefreshEvent();
            if (wasEmailed) {
                handleAdminSuccess(
                    'File uploaded and Email schedules successfully. The email will be dispatched within a few minutes.'
                );
            } else {
                handleAdminSuccess('File uploaded and notice updated successfully.');
            }
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const onFileSelectSubmit = (id, files) => {
        if (!files || files.length === 0) {
            return;
        }
        if (id === 0) {
            set_noticeFile(files[0]);
        } else {
            set_signedFile(files[0]);
        }

        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const onFileSelectCancel = () => {
        set_fileSelectOptions({ ...fileSelectOptions, isOpen: false });
    };

    const openFileSelect = (id) => {
        const options = {
            ownerId: id,
            isOpen: true,
            maxFiles: 1,
            fileTypeName: id === 0 ? 'Notice File' : 'Signed File',
        };
        set_fileSelectOptions(options);
    };

    const requeueNotice = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            set_loadingMessage(`Requeueing Notice...`);
            set_loadingOpen(true);

            set_loadingMessage('Saving...');
            set_loadingOpen(true);
            const response = await noticesApi.requeueNotice(notice.id);
            if (response.statusCode !== 200) {
                return;
            }

            const ln = response.result;
            set_notice(ln);

            handleAdminSuccess('Notice requeued successfully.');
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    const emailNotice = async (noticeId, suppressMessage = false) => {
        try {
            set_loadingMessage(`Requesting Notice Emailing...`);
            set_loadingOpen(true);

            const response = await noticesApi.emailNotice(noticeId);
            if (response.statusCode !== 200) {
                throw new Error('Email request failed unexpectedly!');
            }

            if (!suppressMessage) {
                handleAdminSuccess('Your email request was successful. The email will be dispatched within a few minutes.');
            }
            return true;
        } catch (ex) {
            handleAdminError(ex, 'An unexpected error has occurred. Please try again.');
        } finally {
            set_loadingOpen(false);
        }
    };

    if (!isOpen || !notice) {
        return null;
    }

    return (
        <>
            <Dialog
                maxWidth='sm'
                open={isOpen}
                onClose={_handleClose}
                aria-labelledby='form-dialog-title'
            >
                <DialogContent>
                    <div className='modal-container'>
                        <div className='modal-backdrop'></div>
                        <div
                            className='modal'
                            tabIndex={-1}
                            role='dialog'
                            style={{ width: '700px' }}
                        >
                            <div
                                className='modal-dialog'
                                role='document'
                            >
                                <form name='noticeViewForm'>
                                    <div>
                                        <div
                                            className='page-header'
                                            role='banner'
                                        >
                                            <h1 style={{ whiteSpace: 'nowrap' }}>Notice Details</h1>
                                            <button
                                                type='button'
                                                className='close'
                                                data-dismiss='modal'
                                                aria-label='Close'
                                                onClick={_handleClose}
                                            >
                                                <span aria-hidden='true'>&times;</span>
                                            </button>
                                        </div>
                                        <div>
                                            <div
                                                style={styles.form}
                                                className={'standard-form'}
                                            >
                                                <div
                                                    className='row'
                                                    style={styles.formRow}
                                                >
                                                    <div
                                                        className='columns small-12 large-12'
                                                        style={styles.formColumn}
                                                    >
                                                        <table>
                                                            <tbody>
                                                                <tr style={styles.tablerow}>
                                                                    <td
                                                                        style={{
                                                                            width: '150px',
                                                                        }}
                                                                    >
                                                                        Notice Type:
                                                                    </td>
                                                                    <td>
                                                                        <b>
                                                                            {
                                                                                NoticeTypeOptions.find(
                                                                                    (f) => f.id === notice.noticeTypeId
                                                                                ).name
                                                                            }
                                                                        </b>
                                                                    </td>
                                                                </tr>
                                                                <tr style={styles.tablerow}>
                                                                    <td
                                                                        style={{
                                                                            width: '150px',
                                                                        }}
                                                                    >
                                                                        Status:
                                                                    </td>
                                                                    <td
                                                                        style={{
                                                                            color: NoticeStatusTypeOptions.find(
                                                                                (f) => f.id === notice.statusId
                                                                            ).color,
                                                                        }}
                                                                    >
                                                                        <b>
                                                                            {
                                                                                NoticeStatusTypeOptions.find(
                                                                                    (f) => f.id === notice.statusId
                                                                                ).name
                                                                            }
                                                                        </b>
                                                                    </td>
                                                                </tr>
                                                                {notice.statusId === NoticeStatusType.Error && (
                                                                    <tr style={styles.tablerow}>
                                                                        <td
                                                                            style={{
                                                                                width: '150px',
                                                                            }}
                                                                        >
                                                                            Error:
                                                                        </td>
                                                                        <td
                                                                            style={{
                                                                                color: 'darkred',
                                                                            }}
                                                                        >
                                                                            <b>{notice.error}</b>
                                                                            <br />
                                                                            {notice.statusId === NoticeStatusType.Error && (
                                                                                <button
                                                                                    type='button'
                                                                                    className='button hollow'
                                                                                    title='Reset error and attempt Notice re-generation and email'
                                                                                    onClick={(e) => requeueNotice(e)}
                                                                                    style={{
                                                                                        marginTop: '15px',
                                                                                    }}
                                                                                >
                                                                                    Reset Error and Requeue
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                <tr style={styles.tablerow}>
                                                                    <td>Notice Date: </td>
                                                                    <td>
                                                                        <b>
                                                                            {notice.noticeDate &&
                                                                                formatDate(new Date(notice.noticeDate))}
                                                                        </b>
                                                                    </td>
                                                                </tr>
                                                                <tr style={styles.tablerow}>
                                                                    <td>Created:</td>
                                                                    <td>
                                                                        <b>{notice.isManual ? 'Manually' : 'Automatically'}</b>
                                                                    </td>
                                                                </tr>
                                                                <tr style={styles.tablerow}>
                                                                    <td>Sent By:</td>
                                                                    <td>
                                                                        <b>{notice.sentByName ? notice.sentByName : 'System'}</b>
                                                                    </td>
                                                                </tr>
                                                                {notice.sentToEmail && (
                                                                    <tr style={styles.tablerow}>
                                                                        <td>Sent To:</td>
                                                                        <td>
                                                                            <b>{notice.sentToName}</b> <br /> {notice.sentToEmail}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                <tr style={styles.tablerow}>
                                                                    <td>Was Read:</td>
                                                                    <td>
                                                                        <b>
                                                                            {notice.isManual
                                                                                ? 'Not applicable for Manual Notice'
                                                                                : notice.isRead
                                                                                  ? 'Yes'
                                                                                  : 'No'}
                                                                        </b>
                                                                    </td>
                                                                </tr>
                                                                {!notice.isManual && notice.eSignTypeId !== 0 && (
                                                                    <tr style={styles.tablerow}>
                                                                        <td>Signed:</td>
                                                                        <td>
                                                                            {notice.eSignDate && (
                                                                                <b>
                                                                                    {formatDateTime(new Date(notice.eSignDate))}
                                                                                </b>
                                                                            )}
                                                                            {notice.eSignName && (
                                                                                <div>
                                                                                    {' '}
                                                                                    by <b>{notice.eSignName}</b>
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}

                                                                {notice.addresses && (
                                                                    <tr style={styles.tablerow}>
                                                                        <td>Address:</td>
                                                                        <td>
                                                                            {notice.addresses.map((a, index) => {
                                                                                return (
                                                                                    <div
                                                                                        key={'address' + index}
                                                                                        style={{
                                                                                            marginBottom: '5px',
                                                                                        }}
                                                                                    >
                                                                                        {a.street1} {a.street2}
                                                                                        <br />
                                                                                        {a.city}, {a.state} {a.zipCode}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                                <tr style={styles.tablerow}>
                                                                    <td>Notice File: </td>
                                                                    <td>
                                                                        <div>
                                                                            {notice.noticeFile && (
                                                                                <div>
                                                                                    <a
                                                                                        href={notice.noticeFile.url}
                                                                                        target='_blank'
                                                                                        title='Download'
                                                                                        rel='noreferrer'
                                                                                        style={{
                                                                                            cursor: 'pointer',
                                                                                        }}
                                                                                    >
                                                                                        {notice.noticeFile.name}{' '}
                                                                                        <FontAwesomeIcon
                                                                                            icon={faDownload}
                                                                                            aria-hidden='true'
                                                                                        />
                                                                                    </a>
                                                                                </div>
                                                                            )}

                                                                            <div>
                                                                                <button
                                                                                    type='button'
                                                                                    className='button hollow'
                                                                                    title='Select file to upload...'
                                                                                    onClick={(e) => openFileSelect(0)}
                                                                                    style={{
                                                                                        marginTop: '15px',
                                                                                    }}
                                                                                >
                                                                                    {notice.noticeFile
                                                                                        ? 'Replace File'
                                                                                        : 'Select File'}
                                                                                </button>
                                                                                &nbsp;
                                                                                {noticeFile && (
                                                                                    <span
                                                                                        style={{
                                                                                            color: 'darkred',
                                                                                        }}
                                                                                    >
                                                                                        &nbsp;&nbsp;
                                                                                        {noticeFile.name}
                                                                                        &nbsp;
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                                {notice.signedFile && (
                                                                    <tr style={styles.tablerow}>
                                                                        <td>Signed File</td>
                                                                        <td>
                                                                            <div>
                                                                                <a
                                                                                    href={notice.signedFile.url}
                                                                                    target='_blank'
                                                                                    title='Download'
                                                                                    rel='noreferrer'
                                                                                    style={{
                                                                                        cursor: 'pointer',
                                                                                    }}
                                                                                >
                                                                                    {notice.signedFile.name}{' '}
                                                                                    <FontAwesomeIcon
                                                                                        icon={faDownload}
                                                                                        aria-hidden='true'
                                                                                    />
                                                                                </a>
                                                                                <span>
                                                                                    &nbsp;&nbsp;
                                                                                    <button
                                                                                        type='button'
                                                                                        className='button hollow'
                                                                                        title='Replace Signed File'
                                                                                        onClick={(e) => openFileSelect(1)}
                                                                                        style={{
                                                                                            marginTop: '15px',
                                                                                        }}
                                                                                    >
                                                                                        Select New File
                                                                                    </button>
                                                                                    &nbsp;
                                                                                </span>
                                                                            </div>
                                                                            {signedFile && (
                                                                                <div
                                                                                    style={{
                                                                                        color: 'darkred',
                                                                                    }}
                                                                                >
                                                                                    {signedFile.name}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div
                                                    style={{
                                                        padding: '5px 5px',
                                                    }}
                                                >
                                                    <div
                                                        className='button-footer'
                                                        style={styles.buttonContainer}
                                                    >
                                                        {(noticeFile || signedFile) && (
                                                            <>
                                                                <button
                                                                    type='button'
                                                                    className='button primary'
                                                                    disabled={!canSubmitUpload()}
                                                                    onClick={(e) => uploadFileAndEmail(e, false)}
                                                                >
                                                                    Upload File
                                                                </button>
                                                                {notice.canEmail && (
                                                                    <>
                                                                        &nbsp;
                                                                        <button
                                                                            type='button'
                                                                            className='button hollow'
                                                                            disabled={!canSubmitUpload()}
                                                                            title='Resend notice with new file'
                                                                            onClick={(e) => uploadFileAndEmail(e, true)}
                                                                        >
                                                                            Upload File & Email Notice
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </>
                                                        )}
                                                        {!canSubmitUpload() && (
                                                            <>
                                                                &nbsp;
                                                                <button
                                                                    type='button'
                                                                    className='button hollow'
                                                                    title='Email Notice to principal'
                                                                    onClick={(e) => emailNotice(notice.id)}
                                                                    disabled={!notice.canEmail || !notice.noticeFile}
                                                                >
                                                                    {notice.statusId === NoticeStatusType.Emailed
                                                                        ? 'Email Again'
                                                                        : 'Email'}
                                                                </button>
                                                            </>
                                                        )}
                                                        &nbsp;
                                                        <button
                                                            type='button'
                                                            className={noticeFile || signedFile ? 'button hollow' : 'button'}
                                                            data-dismiss='modal'
                                                            onClick={_handleClose}
                                                            title='Close Notice window'
                                                        >
                                                            Close
                                                        </button>
                                                        &nbsp;
                                                        <button
                                                            type='button'
                                                            className='button hollow'
                                                            data-dismiss='modal'
                                                            onClick={_refresh}
                                                            title='Refresh Notice status'
                                                        >
                                                            Refresh
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            <LoadPanel
                hideOnOutsideClick={false}
                visible={loadingOpen}
                message={loadingMessage}
            />
            <FileSelectDialog
                options={fileSelectOptions}
                onSubmit={onFileSelectSubmit}
                onCancel={onFileSelectCancel}
            />
        </>
    );
};

export default ViewNoticeModal;
